import React, { useState, useEffect, useRef } from 'react';
import useWebSocket from '../hooks/useWebSocket';
import useTranslation from '../i18n/useTranslation';
import '../styles/pixel-art.css';

const MainMenu = ({ onRoomCreated, onRoomJoined, onRejoinFailed, urlRoomCode, urlSessionID }) => {
  const [playerName, setPlayerName] = useState('');
  const [roomCode, setRoomCode] = useState('');
  const [mode, setMode] = useState('menu'); // 'menu', 'create', 'join'
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [hasAttemptedRejoin, setHasAttemptedRejoin] = useState(false);
  const [isRejoinAttempt, setIsRejoinAttempt] = useState(false);
  const logoVideoRef = useRef(null);

  const { isConnected, reconnecting, reconnectAttempt, createRoom, joinRoom, onMessage, MSG_TYPES, rejoinSession } = useWebSocket();
  const { t } = useTranslation();

  // Set video playback rate to 16 fps (original is 21 fps)
  useEffect(() => {
    if (logoVideoRef.current) {
      // 16/21 = 0.762 playback rate to convert from 21 fps to 16 fps
      logoVideoRef.current.playbackRate = 16 / 21;
    }
  }, [mode]);

  // Auto-rejoin if session ID and room code are available
  useEffect(() => {
    if (urlSessionID && urlRoomCode && urlSessionID.trim() && urlRoomCode.trim() && isConnected && !hasAttemptedRejoin) {
      console.log('[REJOIN] Auto-rejoining session...', { urlSessionID, urlRoomCode, isConnected });
      setIsLoading(true);
      setHasAttemptedRejoin(true);
      setIsRejoinAttempt(true);

      // Set a timeout for rejoin failure
      const rejoinTimeout = setTimeout(() => {
        console.error('[REJOIN] Rejoin timeout - session may be expired or server not responding');
        setIsLoading(false);
        setIsRejoinAttempt(false);
        if (onRejoinFailed) {
          onRejoinFailed();
        }
      }, 10000); // 10 second timeout

      // Store timeout ID to clear it on success
      window.rejoinTimeout = rejoinTimeout;

      rejoinSession(urlSessionID, urlRoomCode);
    } else if (urlRoomCode && urlRoomCode.trim() && !urlSessionID && isConnected && mode === 'menu') {
      // No session, show join form
      console.log('[REJOIN] No session ID, showing join form for room:', urlRoomCode);
      setRoomCode(urlRoomCode);
      setMode('join');
    } else if (urlSessionID && urlRoomCode) {
      console.log('[REJOIN] Waiting for connection...', { isConnected, hasAttemptedRejoin });
    }
  }, [urlRoomCode, urlSessionID, isConnected, mode, hasAttemptedRejoin, rejoinSession, onRejoinFailed]);

  useEffect(() => {
    // Listen for room created (sent both when creating, joining, and rejoining)
    const unsubscribeCreated = onMessage(MSG_TYPES.ROOM_CREATED, (message) => {
      console.log('[REJOIN] ROOM_CREATED received', { mode, isRejoinAttempt, data: message.data });
      setIsLoading(false);

      // Clear rejoin timeout if it exists
      if (window.rejoinTimeout) {
        clearTimeout(window.rejoinTimeout);
        window.rejoinTimeout = null;
      }

      if (message.data) {
        // If we're in create mode, call onRoomCreated
        if (mode === 'create') {
          console.log('[REJOIN] Create mode - calling onRoomCreated');
          onRoomCreated(message.data.roomCode, message.data.playerId, message.data.inviteLink);
        } else if (mode === 'join') {
          // If we're in join mode, pass the room data to onRoomJoined
          console.log('[REJOIN] Join mode - calling onRoomJoined');
          onRoomJoined(message.data.roomCode, message.data.inviteLink, false);
        } else if ((mode === 'menu' && hasAttemptedRejoin) || isRejoinAttempt) {
          // If we're in menu mode but just attempted a rejoin, handle it as a rejoin success
          // Don't transition yet - wait for PLAYER_JOINED or GAME_STATE to determine room vs game
          console.log('[REJOIN] Rejoin mode - ROOM_CREATED received, waiting for PLAYER_JOINED or GAME_STATE');
        }
      }
    });

    // Listen for player joined - indicates we're in lobby
    const unsubscribeJoined = onMessage(MSG_TYPES.PLAYER_JOINED, (message) => {
      console.log('[REJOIN] PLAYER_JOINED received', { mode, isRejoinAttempt, urlRoomCode, data: message.data });
      setIsLoading(false);

      // Clear rejoin timeout if it exists
      if (window.rejoinTimeout) {
        clearTimeout(window.rejoinTimeout);
        window.rejoinTimeout = null;
      }

      if (message.data) {
        if (mode === 'join' || isRejoinAttempt) {
          // Rejoined into lobby (game not started)
          const roomCodeToUse = message.data.roomCode || urlRoomCode;
          console.log('[REJOIN] Rejoined into lobby, navigating to room:', roomCodeToUse);
          setIsRejoinAttempt(false);
          onRoomJoined(roomCodeToUse, null, false);
        }
      }
    });

    // Listen for game state - indicates we're in active game
    const unsubscribeGameState = onMessage(MSG_TYPES.GAME_STATE, (message) => {
      console.log('[REJOIN] GAME_STATE received', { isRejoinAttempt, urlRoomCode, data: message.data });
      setIsLoading(false);

      // Clear rejoin timeout if it exists
      if (window.rejoinTimeout) {
        clearTimeout(window.rejoinTimeout);
        window.rejoinTimeout = null;
      }

      if (isRejoinAttempt && message.data) {
        // Rejoined into active game
        console.log('[REJOIN] Rejoined into active game, navigating to game screen');
        setIsRejoinAttempt(false);
        onRoomJoined(urlRoomCode, null, true);
      }
    });

    // Listen for cards dealt - also indicates game started
    const unsubscribeCardsDealt = onMessage(MSG_TYPES.CARDS_DEALT, (message) => {
      console.log('[REJOIN] CARDS_DEALT received', { isRejoinAttempt, urlRoomCode, data: message.data });

      // Clear rejoin timeout if it exists
      if (window.rejoinTimeout) {
        clearTimeout(window.rejoinTimeout);
        window.rejoinTimeout = null;
      }

      if (isRejoinAttempt && message.data) {
        // Rejoined into game that just started
        console.log('[REJOIN] Rejoined into game (cards dealt), navigating to game screen');
        setIsLoading(false);
        setIsRejoinAttempt(false);
        onRoomJoined(urlRoomCode, null, true);
      }
    });

    // Listen for errors
    const unsubscribeError = onMessage(MSG_TYPES.ERROR, (message) => {
      console.error('[REJOIN] ERROR received', { isRejoinAttempt, error: message.error });
      setIsLoading(false);

      // Clear rejoin timeout if it exists
      if (window.rejoinTimeout) {
        clearTimeout(window.rejoinTimeout);
        window.rejoinTimeout = null;
      }

      // If this is a rejoin error, call the failed callback
      if (isRejoinAttempt) {
        console.error('[REJOIN] Rejoin failed with error:', message.error);
        setIsRejoinAttempt(false);
        if (onRejoinFailed) {
          onRejoinFailed();
        }
      } else {
        setError(message.error || t.errors.anError);
      }
    });

    return () => {
      unsubscribeCreated();
      unsubscribeJoined();
      unsubscribeGameState();
      unsubscribeCardsDealt();
      unsubscribeError();
    };
  }, [onMessage, MSG_TYPES, onRoomCreated, onRoomJoined, onRejoinFailed, mode, hasAttemptedRejoin, isRejoinAttempt, urlRoomCode, t.errors.anError]);

  const handleCreateRoom = (e) => {
    e.preventDefault();
    if (!playerName.trim()) {
      setError(t.errors.enterName);
      return;
    }
    setError('');
    setIsLoading(true);
    createRoom(playerName.trim());
  };

  const handleJoinRoom = (e) => {
    e.preventDefault();
    if (!playerName.trim()) {
      setError(t.errors.enterName);
      return;
    }
    if (!roomCode.trim()) {
      setError(t.errors.enterRoomCode);
      return;
    }
    setError('');
    setIsLoading(true);
    joinRoom(roomCode.trim().toUpperCase(), playerName.trim());
  };

  const renderMenu = () => (
    <div className="main-menu">
      <div className="menu-header">
        <video
          ref={logoVideoRef}
          className="logo-video"
          autoPlay
          loop
          muted
          playsInline
          src="/logo.mp4"
        />
        <h1 className="game-title pixel-text">{t.mainMenu.title}</h1>
        <div className="game-subtitle">{t.mainMenu.subtitle}</div>
      </div>

      <div className="connection-status">
        {isConnected ? (
          <span className="status-connected">{t.mainMenu.connected}</span>
        ) : reconnecting ? (
          <span className="status-reconnecting">{t.connection.reconnecting.replace('{attempt}', reconnectAttempt)}</span>
        ) : (
          <span className="status-disconnected">{t.mainMenu.connecting}</span>
        )}
      </div>

      <div className="menu-buttons">
        <button
          className="menu-button pixel-button"
          onClick={() => setMode('create')}
          disabled={!isConnected}
        >
          {t.mainMenu.createRoom}
        </button>
        <button
          className="menu-button pixel-button"
          onClick={() => setMode('join')}
          disabled={!isConnected}
        >
          {t.mainMenu.joinRoom}
        </button>
      </div>

      <div className="game-info">
        <h3>{t.mainMenu.howToPlay}</h3>
        <ul>
          {t.mainMenu.howToPlayItems.map((item, index) => (
            <li key={index}>{item}</li>
          ))}
        </ul>
      </div>
    </div>
  );

  const renderCreateRoom = () => (
    <div className="main-menu">
      <div className="menu-header">
        <h2 className="pixel-text">{t.createRoom.title}</h2>
      </div>

      <form onSubmit={handleCreateRoom} className="menu-form">
        <div className="form-group">
          <label htmlFor="playerName">{t.createRoom.yourName}</label>
          <input
            type="text"
            id="playerName"
            className="pixel-input"
            value={playerName}
            onChange={(e) => setPlayerName(e.target.value)}
            placeholder={t.createRoom.yourNamePlaceholder}
            maxLength={20}
            autoFocus
            disabled={isLoading}
          />
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="form-buttons">
          <button
            type="submit"
            className="pixel-button pixel-button-primary"
            disabled={isLoading || !isConnected}
          >
            {isLoading ? t.createRoom.creating : t.createRoom.createButton}
          </button>
          <button
            type="button"
            className="pixel-button pixel-button-secondary"
            onClick={() => {
              setMode('menu');
              setError('');
            }}
            disabled={isLoading}
          >
            {t.createRoom.back}
          </button>
        </div>
      </form>
    </div>
  );

  const renderJoinRoom = () => (
    <div className="main-menu">
      <div className="menu-header">
        <h2 className="pixel-text">{t.joinRoom.title}</h2>
      </div>

      <form onSubmit={handleJoinRoom} className="menu-form">
        <div className="form-group">
          <label htmlFor="playerName">{t.joinRoom.yourName}</label>
          <input
            type="text"
            id="playerName"
            className="pixel-input"
            value={playerName}
            onChange={(e) => setPlayerName(e.target.value)}
            placeholder={t.joinRoom.yourNamePlaceholder}
            maxLength={20}
            autoFocus
            disabled={isLoading}
          />
        </div>

        <div className="form-group">
          <label htmlFor="roomCode">{t.joinRoom.roomCode}</label>
          <input
            type="text"
            id="roomCode"
            className="pixel-input room-code-input"
            value={roomCode}
            onChange={(e) => setRoomCode(e.target.value.toUpperCase())}
            placeholder={t.joinRoom.roomCodePlaceholder}
            maxLength={6}
            disabled={isLoading}
          />
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="form-buttons">
          <button
            type="submit"
            className="pixel-button pixel-button-primary"
            disabled={isLoading || !isConnected}
          >
            {isLoading ? t.joinRoom.joining : t.joinRoom.joinButton}
          </button>
          <button
            type="button"
            className="pixel-button pixel-button-secondary"
            onClick={() => {
              setMode('menu');
              setError('');
            }}
            disabled={isLoading}
          >
            {t.joinRoom.back}
          </button>
        </div>
      </form>
    </div>
  );

  return (
    <div className="screen main-menu-screen">
      {mode === 'menu' && renderMenu()}
      {mode === 'create' && renderCreateRoom()}
      {mode === 'join' && renderJoinRoom()}
    </div>
  );
};

export default MainMenu;
